import React, { useEffect, useRef, useState } from "react";
import { Panel } from "../components/Panel";
import { usePlayer } from "../context/PlayerContext";
import { api, apiErr } from "../api/client";
import { Coins, Trophy, Loader2, ShoppingCart, Gift, Package, Disc3, Radio, Hammer, Sparkles, Zap, Wrench, Skull, Crown, Cog, Ticket } from "lucide-react";
import { Sound } from "../game/audio";
import { AdManager } from "../game/ads";
import { toast } from "sonner";

const BETS = [50, 100, 500];

const SCRATCH_ICON = { bolt: Zap, gear: Cog, wrench: Wrench, skull: Skull, crown: Crown };
const scratchColor = (id) =>
  id === "crown" ? "#FFD700" : id === "skull" ? "#A78BFA" : id === "wrench" ? "#34D399" : id === "gear" ? "#22D3EE" : "#64748B";

// Wheel wedge multipliers — MUST match backend gc.WHEEL_WEDGES order (clockwise from top).
const WHEEL = [1.5, 0, 2, 0, 1, 3, 0, 1.5, 5, 0, 2, 10];
const SEG = 360 / WHEEL.length;
const wedgeColor = (m) =>
  m >= 10 ? "#FFD700" : m >= 5 ? "#FB923C" : m >= 3 ? "#A78BFA" : m >= 2 ? "#34D399" : m >= 1.5 ? "#22D3EE" : m >= 1 ? "#64748B" : "#1E293B";
const rarityColor = (r) =>
  r === "jackpot" ? "#FFD700" : r === "epic" ? "#A78BFA" : r === "rare" ? "#34D399" : r === "common" ? "#22D3EE" : "#64748B";

function FeedIcon({ kind }) {
  if (kind === "wheel") return <Disc3 size={13} className="text-amber-300" />;
  return <Package size={13} className="text-amber-300" />;
}

export default function Casino({ onClose, onBuyCoins }) {
  const { profile, applyUser } = usePlayer();
  const [bet, setBet] = useState(50);
  const [tab, setTab] = useState("crate");
  const [busy, setBusy] = useState(false);
  const [board, setBoard] = useState([]);
  const [feed, setFeed] = useState([]);
  const [freeAvail, setFreeAvail] = useState(false);
  const [freeCrateAvail, setFreeCrateAvail] = useState(false);
  const [freeScratchAvail, setFreeScratchAvail] = useState(false);

  // crate state
  const [cratePhase, setCratePhase] = useState("idle"); // idle | smashing | revealed
  const [crateResult, setCrateResult] = useState(null);
  // wheel state
  const [angle, setAngle] = useState(0);
  const [wheelResult, setWheelResult] = useState(null);
  // scratch state
  const [scratchPhase, setScratchPhase] = useState("idle"); // idle | scratching | revealed
  const [scratchCards, setScratchCards] = useState([null, null, null]);
  const [revealed, setRevealed] = useState([false, false, false]);
  const [scratchResult, setScratchResult] = useState(null);
  const spinning = useRef(false);

  const coins = profile?.coins || 0;

  const loadFeed = () => api.casinoFeed().then(({ data }) => { setFeed(data.feed || []); setFreeAvail(!!data.freeSpinAvailable); setFreeCrateAvail(!!data.freeCrateAvailable); setFreeScratchAvail(!!data.freeScratchAvailable); }).catch(() => {});
  useEffect(() => { loadFeed(); const t = setInterval(loadFeed, 5000); return () => clearInterval(t); }, []);
  useEffect(() => { if (tab === "board") api.casinoLeaderboard().then(({ data }) => setBoard(data.top || [])).catch(() => {}); }, [tab, crateResult, wheelResult, scratchResult]);

  const afterWin = (win, jackpot, kind) => {
    if (win > 0) { Sound.levelup?.(); Sound.combo?.(jackpot ? 8 : 3); toast.success(`${jackpot ? "🎉 JACKPOT! " : ""}You won ${win.toLocaleString()} coins!`); }
    else { Sound.crash?.(1.2); }
    loadFeed();
  };

  // ---- Crate Smash ----
  const smashCrate = async () => {
    if (busy || spinning.current) return;
    if (coins < bet) { toast.error("Not enough coins — grab some free ones!"); return; }
    spinning.current = true; setBusy(true);
    setCratePhase("smashing"); setCrateResult(null); setWheelResult(null);
    Sound.engine?.(1);
    try {
      const { data } = await api.casinoCrate(bet);
      await new Promise((r) => setTimeout(r, 900));
      Sound.crash?.(data.jackpot ? 2 : 1.4); if (navigator.vibrate) navigator.vibrate(data.jackpot ? [40, 30, 60] : 30);
      if (data.user) applyUser(data.user);
      setCrateResult({ ...data.tier, win: data.win, jackpot: data.jackpot });
      setCratePhase("revealed");
      afterWin(data.win, data.jackpot, "crate");
    } catch (e) {
      setCratePhase("idle");
      toast.error(apiErr(e, "Smash failed."));
    } finally { setBusy(false); spinning.current = false; }
  };

  // ---- Wreck Wheel ----
  const runWheel = async (fn, isFree) => {
    if (busy || spinning.current) return;
    spinning.current = true; setBusy(true);
    setWheelResult(null); setCrateResult(null);
    Sound.click?.();
    try {
      const { data } = await fn();
      const idx = data.index;
      const targetCenter = idx * SEG + SEG / 2;         // wedge center measured clockwise from top
      const base = angle - (angle % 360);               // normalize
      const final = base + 360 * 6 + (360 - targetCenter); // 6 full spins then align wedge to the top pointer
      setAngle(final);
      await new Promise((r) => setTimeout(r, 4200));     // matches CSS transition
      if (data.user) applyUser(data.user);
      setWheelResult({ mult: data.mult, win: data.win, jackpot: data.jackpot });
      afterWin(data.win, data.jackpot, "wheel");
      if (isFree) loadFeed();
    } catch (e) {
      toast.error(apiErr(e, "Spin failed."));
    } finally { setBusy(false); spinning.current = false; }
  };
  const spinWheel = () => { if (coins < bet) { toast.error("Not enough coins — grab some free ones!"); return; } runWheel(() => api.casinoWheel(bet), false); };
  const freeSpin = () => { setTab("wheel"); runWheel(() => api.casinoFreeSpin(), true); };

  // ---- Demolition Scratcher ----
  const revealScratch = async (dataPromise, opts = {}) => {
    if (busy || spinning.current) return;
    spinning.current = true; setBusy(true);
    setScratchPhase("scratching"); setScratchResult(null); setCrateResult(null); setWheelResult(null);
    setRevealed([false, false, false]); setScratchCards([null, null, null]);
    Sound.click?.();
    try {
      const { data } = await dataPromise();
      setScratchCards(data.symbols);
      for (let i = 0; i < 3; i++) {
        await new Promise((r) => setTimeout(r, 480));
        Sound.crash?.(0.8); if (navigator.vibrate) navigator.vibrate(15);
        setRevealed((prev) => { const n = [...prev]; n[i] = true; return n; });
      }
      await new Promise((r) => setTimeout(r, 350));
      if (data.user) applyUser(data.user);
      setScratchResult({ win: data.win, jackpot: data.jackpot, label: data.tier.label });
      setScratchPhase("revealed");
      afterWin(data.win, data.jackpot, "scratch");
      if (opts.free) loadFeed();
    } catch (e) {
      setScratchPhase("idle");
      toast.error(apiErr(e, "Scratch failed."));
    } finally { setBusy(false); spinning.current = false; }
  };
  const runScratch = () => { if (coins < bet) { toast.error("Not enough coins — grab some free ones!"); return; } revealScratch(() => api.casinoScratch(bet)); };
  const freeScratch = () => { setTab("scratch"); revealScratch(() => api.casinoFreeScratch(), { free: true }); };

  const watchForCoins = async () => {
    if (busy) return; setBusy(true);
    try { await AdManager.playRewarded(); const { data } = await api.freeCoins(); if (data.user) applyUser(data.user); toast.success(`+${data.granted} free coins!`); }
    catch (e) { toast.error(apiErr(e, "Try again shortly.")); }
    finally { setBusy(false); }
  };

  const claimFreeCrate = async () => {
    if (busy) return; setBusy(true);
    try {
      await AdManager.playRewarded();
      const { data } = await api.casinoFreeCrate();
      if (data.user) applyUser(data.user);
      setTab("crate"); setWheelResult(null);
      setCrateResult({ ...data.tier, win: data.win, jackpot: data.jackpot }); setCratePhase("revealed");
      setFreeCrateAvail(false);
      afterWin(data.win, data.jackpot, "crate");
    } catch (e) { toast.error(apiErr(e, "Free crate used — back tomorrow!")); }
    finally { setBusy(false); }
  };

  const claimCoinBoost = async () => {
    if (busy) return; setBusy(true);
    try {
      await AdManager.playRewarded();
      const { data } = await api.adsCoinBoost();
      if (data.user) applyUser(data.user);
      const n = data.coinBoostMatches;
      toast.success(`2× coins active for your next ${n} match${n > 1 ? "es" : ""}!`);
    } catch (e) { toast.error(apiErr(e, "Boost maxed out — go play a match!")); }
    finally { setBusy(false); }
  };

  return (
    <Panel title="Casino" onClose={onClose}>
      {/* Live jackpot ticker */}
      <div className="rounded-xl bg-black/50 border border-amber-500/30 overflow-hidden mb-3 flex items-center" data-testid="jackpot-feed">
        <div className="shrink-0 px-2.5 py-1.5 bg-amber-500 text-black font-black text-[10px] uppercase flex items-center gap-1"><Radio size={12} /> Live Wins</div>
        <div className="relative flex-1 overflow-hidden h-7">
          {feed.length === 0 ? (
            <div className="px-3 h-7 flex items-center text-slate-500 text-xs">Be the first to hit a jackpot!</div>
          ) : (
            <div className="ticker flex items-center gap-6 whitespace-nowrap px-3 h-7">
              {[...feed, ...feed].map((f, i) => (
                <span key={i} className="text-xs text-slate-200 flex items-center gap-1.5">
                  <FeedIcon kind={f.kind} /><b className="text-amber-300">{f.name}</b> won <b className="text-green-400">{(f.win || 0).toLocaleString()}</b>{f.jackpot ? <span className="text-amber-400 font-black">JACKPOT</span> : null}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="card px-3 py-1.5 flex items-center gap-1.5" data-testid="casino-balance">
          <Coins size={16} className="text-amber-400" /><span className="font-black text-white">{coins.toLocaleString()}</span>
        </div>
        <div className="flex gap-1 bg-slate-900 rounded-xl p-1">
          <button onClick={() => setTab("crate")} className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 ${tab === "crate" ? "bg-amber-500 text-black" : "text-slate-300"}`} data-testid="casino-tab-crate"><Package size={13} /> Crate</button>
          <button onClick={() => setTab("wheel")} className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 ${tab === "wheel" ? "bg-amber-500 text-black" : "text-slate-300"}`} data-testid="casino-tab-wheel"><Disc3 size={13} /> Wheel</button>
          <button onClick={() => setTab("scratch")} className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 ${tab === "scratch" ? "bg-amber-500 text-black" : "text-slate-300"}`} data-testid="casino-tab-scratch"><Ticket size={13} /> Scratch</button>
          <button onClick={() => setTab("board")} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${tab === "board" ? "bg-amber-500 text-black" : "text-slate-300"}`} data-testid="casino-tab-board">Winners</button>
        </div>
      </div>

      {/* ---------- CRATE SMASH ---------- */}
      {tab === "crate" && (
        <>
          <div className="rounded-2xl bg-gradient-to-b from-slate-800 to-slate-950 border-2 border-amber-500/40 p-5 mb-3 flex flex-col items-center min-h-[220px] justify-center" data-testid="crate-stage">
            {cratePhase === "revealed" && crateResult ? (
              <div className="text-center burst-in" data-testid="crate-result">
                {crateResult.win > 0 ? (
                  <>
                    <Sparkles size={40} className="mx-auto mb-1" style={{ color: rarityColor(crateResult.rarity) }} />
                    <div className="font-head font-black text-3xl" style={{ color: rarityColor(crateResult.rarity) }}>{crateResult.label}</div>
                    <div className="text-green-400 font-black text-2xl mt-1 flex items-center justify-center gap-1"><Coins size={22} /> +{crateResult.win.toLocaleString()}</div>
                  </>
                ) : (
                  <>
                    <Package size={40} className="mx-auto mb-1 text-slate-500" />
                    <div className="font-head font-black text-2xl text-slate-400">Busted!</div>
                    <div className="text-slate-500 text-sm mt-1">Empty crate — smash another one!</div>
                  </>
                )}
              </div>
            ) : (
              <div className={`text-center ${cratePhase === "smashing" ? "crate-shake" : ""}`}>
                <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-amber-600 to-yellow-800 border-4 border-amber-400/60 flex items-center justify-center shadow-[0_0_30px_rgba(255,180,0,0.4)]">
                  <Package size={64} className="text-amber-200" />
                </div>
                <div className="text-slate-400 text-xs mt-3">{cratePhase === "smashing" ? "Cracking it open…" : "Smash to reveal a reward"}</div>
              </div>
            )}
          </div>

          <div className="flex gap-2 mb-2">
            {BETS.map((b) => (
              <button key={b} onClick={() => setBet(b)} className={`flex-1 card py-2 font-bold text-sm transition-colors ${bet === b ? "border-amber-500 text-amber-400" : "text-slate-300"}`} data-testid={`bet-${b}`}>{b}</button>
            ))}
          </div>

          <button onClick={smashCrate} disabled={busy} className="btn-gold w-full py-3.5 text-lg flex items-center justify-center gap-2 disabled:opacity-50" data-testid="crate-smash">
            {busy && cratePhase === "smashing" ? <Loader2 size={20} className="animate-spin" /> : <Hammer size={20} />} SMASH CRATE ({bet})
          </button>

          <div className="mt-3 card p-2.5" data-testid="crate-odds">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1.5">Possible Rewards</div>
            <div className="grid grid-cols-4 gap-1.5">
              {[{ l: "1.5x", r: "common" }, { l: "2x", r: "rare" }, { l: "3x", r: "rare" }, { l: "5x", r: "epic" }, { l: "20x", r: "jackpot" }].map((t) => (
                <div key={t.l} className="text-center bg-black/30 rounded-lg py-1 font-black text-xs" style={{ color: rarityColor(t.r) }}>{t.l}</div>
              ))}
              <div className="col-span-3 text-center bg-black/20 rounded-lg py-1 text-[11px] text-slate-500 flex items-center justify-center">Careful — some crates are empty!</div>
            </div>
          </div>
        </>
      )}

      {/* ---------- WRECK WHEEL ---------- */}
      {tab === "wheel" && (
        <>
          <div className="rounded-2xl bg-gradient-to-b from-slate-800 to-slate-950 border-2 border-amber-500/40 p-4 mb-3 flex flex-col items-center" data-testid="wheel-stage">
            <div className="relative" style={{ width: 210, height: 210 }}>
              {/* pointer */}
              <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-10" style={{ width: 0, height: 0, borderLeft: "11px solid transparent", borderRight: "11px solid transparent", borderTop: "20px solid #FFD700" }} />
              <svg width="210" height="210" viewBox="0 0 100 100" style={{ transform: `rotate(${angle}deg)`, transition: "transform 4.2s cubic-bezier(.15,.9,.2,1)" }} data-testid="wheel-svg">
                {WHEEL.map((m, i) => {
                  const a0 = (i * SEG - 90) * Math.PI / 180;
                  const a1 = ((i + 1) * SEG - 90) * Math.PI / 180;
                  const x0 = 50 + 50 * Math.cos(a0), y0 = 50 + 50 * Math.sin(a0);
                  const x1 = 50 + 50 * Math.cos(a1), y1 = 50 + 50 * Math.sin(a1);
                  const mid = ((i + 0.5) * SEG - 90) * Math.PI / 180;
                  const tx = 50 + 33 * Math.cos(mid), ty = 50 + 33 * Math.sin(mid);
                  return (
                    <g key={i}>
                      <path d={`M50 50 L${x0} ${y0} A50 50 0 0 1 ${x1} ${y1} Z`} fill={wedgeColor(m)} stroke="#0b0f1a" strokeWidth="0.6" />
                      <text x={tx} y={ty} fontSize="7" fontWeight="900" fill={m === 0 ? "#64748B" : "#0b0f1a"} textAnchor="middle" dominantBaseline="central" transform={`rotate(${(i + 0.5) * SEG} ${tx} ${ty})`}>{m === 0 ? "✕" : `${m}x`}</text>
                    </g>
                  );
                })}
                <circle cx="50" cy="50" r="8" fill="#0b0f1a" stroke="#FFD700" strokeWidth="1.5" />
              </svg>
            </div>
            {wheelResult && (
              <div className="text-center mt-3 burst-in" data-testid="wheel-result">
                {wheelResult.win > 0 ? (
                  <div className={`font-head font-black ${wheelResult.jackpot ? "text-amber-300 text-2xl" : "text-green-400 text-xl"} flex items-center justify-center gap-1`}>
                    {wheelResult.jackpot ? "JACKPOT " : ""}<Coins size={20} /> +{wheelResult.win.toLocaleString()}
                  </div>
                ) : (
                  <div className="text-slate-400 font-bold">Missed! Spin again.</div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-2 mb-2">
            {BETS.map((b) => (
              <button key={b} onClick={() => setBet(b)} className={`flex-1 card py-2 font-bold text-sm transition-colors ${bet === b ? "border-amber-500 text-amber-400" : "text-slate-300"}`} data-testid={`wheel-bet-${b}`}>{b}</button>
            ))}
          </div>

          <button onClick={spinWheel} disabled={busy} className="btn-gold w-full py-3.5 text-lg flex items-center justify-center gap-2 disabled:opacity-50" data-testid="wheel-spin">
            {busy ? <Loader2 size={20} className="animate-spin" /> : <Disc3 size={20} />} SPIN WHEEL ({bet})
          </button>
        </>
      )}

      {/* ---------- DEMOLITION SCRATCHER ---------- */}
      {tab === "scratch" && (
        <>
          <div className="rounded-2xl bg-gradient-to-b from-slate-800 to-slate-950 border-2 border-amber-500/40 p-5 mb-3 flex flex-col items-center min-h-[220px] justify-center" data-testid="scratch-stage">
            <div className="grid grid-cols-3 gap-3">
              {[0, 1, 2].map((i) => {
                const sym = scratchCards[i];
                const Icon = sym ? SCRATCH_ICON[sym] : Hammer;
                const shown = revealed[i] && sym;
                return (
                  <div key={i} className="w-[74px] h-[92px] rounded-xl border-2 border-amber-500/40 overflow-hidden flex items-center justify-center relative bg-slate-900" data-testid={`scratch-card-${i}`}>
                    {shown ? (
                      <div className="scratch-reveal" style={{ color: scratchColor(sym), filter: `drop-shadow(0 0 8px ${scratchColor(sym)})` }}>
                        <Icon size={38} />
                      </div>
                    ) : (
                      <div className="scratch-cover absolute inset-0 flex items-center justify-center text-slate-200 font-black text-lg">?</div>
                    )}
                  </div>
                );
              })}
            </div>
            {scratchPhase === "revealed" && scratchResult ? (
              <div className="text-center mt-3 burst-in" data-testid="scratch-result">
                {scratchResult.win > 0 ? (
                  <div className={`font-head font-black ${scratchResult.jackpot ? "text-amber-300 text-2xl" : "text-green-400 text-xl"} flex items-center justify-center gap-1`}>
                    {scratchResult.jackpot ? "JACKPOT " : ""}<Coins size={20} /> +{scratchResult.win.toLocaleString()}
                  </div>
                ) : (
                  <div className="text-slate-400 font-bold">No match — scratch again!</div>
                )}
              </div>
            ) : (
              <div className="text-slate-400 text-xs mt-3">{scratchPhase === "scratching" ? "Scratching…" : "Match all 3 symbols to win big!"}</div>
            )}
          </div>

          <div className="flex gap-2 mb-2">
            {BETS.map((b) => (
              <button key={b} onClick={() => setBet(b)} className={`flex-1 card py-2 font-bold text-sm transition-colors ${bet === b ? "border-amber-500 text-amber-400" : "text-slate-300"}`} data-testid={`scratch-bet-${b}`}>{b}</button>
            ))}
          </div>

          <button onClick={runScratch} disabled={busy} className="btn-gold w-full py-3.5 text-lg flex items-center justify-center gap-2 disabled:opacity-50" data-testid="scratch-play">
            {busy ? <Loader2 size={20} className="animate-spin" /> : <Ticket size={20} />} SCRATCH CARD ({bet})
          </button>

          <div className="mt-3 card p-2.5" data-testid="scratch-odds">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1.5">Match 3 to win</div>
            <div className="grid grid-cols-5 gap-1.5">
              {[{ id: "bolt", l: "1.5x" }, { id: "gear", l: "2x" }, { id: "wrench", l: "4x" }, { id: "skull", l: "8x" }, { id: "crown", l: "25x" }].map((t) => {
                const Icon = SCRATCH_ICON[t.id];
                return (
                  <div key={t.id} className="text-center bg-black/30 rounded-lg py-1.5 flex flex-col items-center gap-0.5" style={{ color: scratchColor(t.id) }}>
                    <Icon size={16} /><span className="font-black text-[11px]">{t.l}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* shared actions (crate + wheel) */}
      {tab !== "board" && (
        <>
          <button onClick={freeSpin} disabled={!freeAvail || busy} className="btn-ghost w-full py-2.5 mt-2 text-sm flex items-center justify-center gap-1.5 border-green-500/40 text-green-300 disabled:opacity-40" data-testid="casino-free-spin">
            <Gift size={15} /> {freeAvail ? "DAILY FREE WHEEL SPIN" : "Free spin used — back tomorrow"}
          </button>
          <button onClick={freeScratch} disabled={!freeScratchAvail || busy} className="btn-ghost w-full py-2.5 mt-2 text-sm flex items-center justify-center gap-1.5 border-amber-500/40 text-amber-300 disabled:opacity-40" data-testid="casino-free-scratch">
            <Ticket size={15} /> {freeScratchAvail ? "DAILY FREE SCRATCH CARD" : "Free scratch used — back tomorrow"}
          </button>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button onClick={claimFreeCrate} disabled={!freeCrateAvail || busy} className="btn-ghost py-2.5 text-sm flex items-center justify-center gap-1.5 border-amber-500/40 text-amber-300 disabled:opacity-40" data-testid="casino-free-crate">
              <Package size={15} /> {freeCrateAvail ? "Free Crate (Ad)" : "Crate used"}
            </button>
            <button onClick={claimCoinBoost} disabled={busy} className="btn-ghost py-2.5 text-sm flex items-center justify-center gap-1.5 border-fuchsia-500/40 text-fuchsia-300 disabled:opacity-50" data-testid="casino-coin-boost">
              <Sparkles size={15} /> 2× Coins (Ad)
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button onClick={watchForCoins} disabled={busy} className="btn-ghost py-2.5 text-sm flex items-center justify-center gap-1.5 border-cyan-500/40 text-cyan-300 disabled:opacity-50" data-testid="casino-free-coins">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Gift size={15} />} Free Coins (Ad)
            </button>
            <button onClick={onBuyCoins} className="btn-ghost py-2.5 text-sm flex items-center justify-center gap-1.5 border-amber-500/40 text-amber-300" data-testid="casino-buy-coins">
              <ShoppingCart size={15} /> Buy Coins
            </button>
          </div>
        </>
      )}

      {/* ---------- LEADERBOARD ---------- */}
      {tab === "board" && (
        <div data-testid="casino-leaderboard">
          <div className="text-xs uppercase tracking-widest text-amber-400 font-bold mb-2 flex items-center gap-1"><Trophy size={13} /> Biggest Casino Winners</div>
          {board.length === 0 && <div className="text-slate-500 text-sm py-6 text-center">No winners yet — hit a jackpot to lead!</div>}
          {board.map((r, i) => (
            <div key={i} className="flex items-center justify-between card px-3 py-2 mb-1.5" data-testid={`casino-rank-${i}`}>
              <div className="flex items-center gap-2">
                <span className={`font-black w-6 text-center ${i === 0 ? "text-amber-400" : "text-slate-400"}`}>{i + 1}</span>
                <span className="text-white font-bold">{r.displayName}</span>
              </div>
              <span className="flex items-center gap-1 text-green-400 font-black"><Coins size={13} /> +{(r.net || 0).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
